import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { fetchDonationTarget } from "../Donations/donationEngineApi";
import { normalizeExternalUrl } from "../Helper/urls";
import {
  resolveMediaUrl,
  resolveMediaBackground,
  handleMediaImgError,
} from "../Helper/mediaUrl";
import {
  entityPublicPath,
  entityDonatePath,
  entityDonateCheckoutPath,
} from "../Helper/entityRoutes";
import { AppearanceButton } from "../Appearance";
import EntityIdentity from "../Helper/EntityIdentity";
import EmptyState from "../Dashboard/EmptyState";
import "./EntityDonatePage.css";

/**
 * Focused entity donation landing.
 * Identity + Continue Donation → canonical checkout hierarchy.
 */
function resolveParams(match, location) {
  const p = (match && match.params) || {};
  let entityType = (p.entityType || "").toLowerCase();
  let slug = p.slug || "";

  if (!entityType && location && location.pathname) {
    const parts = location.pathname.replace(/^\//, "").split("/");
    if (parts.length >= 3 && parts[2] === "donate") {
      const map = {
        organizations: "organization",
        creators: "creator",
        projects: "project",
        organization: "organization",
        org: "organization",
        creator: "creator",
        project: "project",
      };
      entityType = map[parts[0]] || parts[0];
      slug = parts[1];
    }
  }

  if (entityType === "org") entityType = "organization";
  return { entityType: entityType || "organization", slug };
}

function backLabel(entityType) {
  if (entityType === "creator") return "Back to Creator";
  if (entityType === "project") return "Back to Project";
  return "Back to Organization";
}

function EntityDonatePage({ match, location }) {
  const { entityType, slug } = useMemo(
    () => resolveParams(match, location),
    [match, location]
  );
  const [loading, setLoading] = useState(true);
  const [target, setTarget] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(false);
      const data = await fetchDonationTarget(entityType, slug);
      if (cancelled) return;
      if (!data || data.found === false) {
        setTarget(null);
        setError(true);
        setLoading(false);
        return;
      }
      setTarget(data);
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [entityType, slug]);

  if (loading) {
    return (
      <div className="main-sec-content entity-donate-page" role="status">
        <EmptyState
          title="Loading…"
          message="Preparing donation details."
          statusRole="status"
        />
      </div>
    );
  }

  if (error || !target) {
    return (
      <div className="main-sec-content entity-donate-page">
        <EmptyState
          title="Donation page unavailable"
          message="We couldn't find that community entity."
        >
          <Link to="/community" className="ae-btn ae-btn--primary">
            Back to Community
          </Link>
        </EmptyState>
      </div>
    );
  }

  const branding = target.branding || {};
  const name = branding.title || target.title || "Community partner";
  const handle = target.slug || slug || "";
  const mode = target.resolved_mode || target.mode || "internal";
  const primaryColor =
    branding.primary_color || target.primary_color || "#e50914";
  const logoRaw = branding.logo || target.logo;
  const coverRaw = branding.cover || target.image;
  const logo = logoRaw ? resolveMediaUrl(logoRaw) : "";
  const coverStyle = coverRaw
    ? { backgroundImage: resolveMediaBackground(coverRaw) }
    : undefined;
  const externalUrl =
    mode === "external"
      ? normalizeExternalUrl(target.external_url || target.donation_url || "")
      : "";

  const backPath = entityPublicPath(entityType, handle || slug);
  const landingPath = entityDonatePath(entityType, handle || slug);
  const checkoutPath = entityDonateCheckoutPath(entityType, handle || slug);

  return (
    <div
      className="main-sec-content entity-donate-page"
      style={{ "--donate-accent": primaryColor }}
      data-entity-type={entityType}
    >
      <Helmet>
        <title>{`Donate — ${name}`}</title>
        <link
          rel="canonical"
          href={
            (typeof window !== "undefined" ? window.location.origin : "") +
            landingPath
          }
        />
      </Helmet>

      <div
        className={
          "entity-donate-stage" +
          (coverStyle ? " entity-donate-stage--cover" : "")
        }
        style={coverStyle}
        aria-hidden={coverStyle ? true : undefined}
      />

      <div className="entity-donate-shell">
        <article
          className="entity-donate-card"
          aria-labelledby="entity-donate-title"
        >
          {logo ? (
            <img
              src={logo}
              alt={`${name} profile`}
              className="entity-donate-logo"
              onError={handleMediaImgError}
            />
          ) : (
            <div
              className="entity-donate-logo-fallback"
              role="img"
              aria-label={`${name} profile`}
            >
              <i
                className={
                  entityType === "creator"
                    ? "fas fa-user"
                    : entityType === "project"
                    ? "fas fa-folder-open"
                    : "fas fa-building"
                }
                aria-hidden="true"
              />
            </div>
          )}

          <EntityIdentity
            name={name}
            handle={handle}
            typeLabel={
              entityType === "creator"
                ? "Creator"
                : entityType === "project"
                ? "Project"
                : "Organization"
            }
            nameId="entity-donate-title"
            variant="checkout"
            className="entity-donate-identity"
          />

          {mode === "external" && externalUrl ? (
            <AppearanceButton
              href={externalUrl}
              variant="donate"
              size="lg"
              className="entity-donate-cta"
              target="_blank"
              rel="noopener noreferrer"
            >
              Continue Donation
            </AppearanceButton>
          ) : (
            <AppearanceButton
              to={checkoutPath}
              variant="donate"
              size="lg"
              className="entity-donate-cta"
            >
              Continue Donation
            </AppearanceButton>
          )}

          <p className="entity-donate-back">
            <Link to={backPath}>{backLabel(entityType)}</Link>
          </p>
        </article>
      </div>
    </div>
  );
}

export default EntityDonatePage;
